/**
 * Tags for shared Strapi collection entries referenced inside page blocks.
 *
 * When a collection entry is published (e.g. pricing-cards-group), the webhook
 * revalidates `{contentType}-{documentId}`. Pages that embed that relation get
 * the same tag at fetch time via collectEntityTags — no rebuild needed.
 *
 * Format must match entityTagFromUid() webhook output.
 * Add a row to RELATION_FIELD_CONTENT_TYPE when field name ≠ content type slug.
 */
const RELATION_FIELD_CONTENT_TYPE: Record<string, string> = {
  pricing_cards_group: 'pricing-cards-group',
  product_cards_groups: 'product-cards-group',
  features_references: 'feature-list',
  contactSalesCards: 'contact-sales-card',
};

const LAYOUT_REFERENCE_FIELDS = new Set([
  'header_reference',
  'footer_reference',
]);

function relationFieldToContentType(field: string): string {
  if (field in RELATION_FIELD_CONTENT_TYPE) {
    return RELATION_FIELD_CONTENT_TYPE[field];
  }

  return field.replace(/_/g, '-').replace(/s$/, '');
}

function extractDocumentIds(data: unknown): string[] {
  if (!data) {
    return [];
  }

  if (Array.isArray(data)) {
    return data.flatMap(extractDocumentIds);
  }

  if (typeof data === 'object' && 'documentId' in data) {
    const documentId = (data as { documentId?: string }).documentId;
    return documentId ? [documentId] : [];
  }

  return [];
}

function collectFromValue(value: unknown, collected: Set<string>): void {
  if (!value || typeof value !== 'object') {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectFromValue(item, collected));
    return;
  }

  for (const [field, child] of Object.entries(
    value as Record<string, unknown>
  )) {
    if (LAYOUT_REFERENCE_FIELDS.has(field)) {
      continue;
    }

    if (child && typeof child === 'object' && 'data' in child) {
      const documentIds = extractDocumentIds(
        (child as { data?: unknown }).data
      );

      if (documentIds.length > 0) {
        const contentType = relationFieldToContentType(field);
        documentIds.forEach((documentId) =>
          collected.add(`${contentType}-${documentId}`)
        );
      }
    }

    collectFromValue(child, collected);
  }
}

export function collectEntityTags(blocks: unknown): string[] {
  const collected = new Set<string>();
  collectFromValue(blocks, collected);
  return Array.from(collected);
}

/** Maps Strapi webhook uid → revalidation tag. e.g. api::pricing-cards-group.pricing-cards-group + abc → pricing-cards-group-abc */
export function entityTagFromUid(
  uid: string,
  documentId: string
): string | null {
  const match = uid.match(/^api::([^.]+)\.\1$/);
  if (!match) {
    return null;
  }

  return `${match[1]}-${documentId}`;
}
